<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, onUnmounted, reactive, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  ImageOptions,
  UploadQuery,
  UploadSortDirection,
  UploadSortField,
} from '@/generated/graphql'
import EditUpload from '@/components/upload/EditUpload.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import UploadCard from '@/components/upload/UploadCard.vue'
import UploadSearch from '@/components/upload/UploadSearch.vue'
import UploadButton from '@/components/upload/UploadButton.vue'
import { UpdateUpload } from '@/generated/graphql'
import { uploadUsageLocationDisplayDetailsFragments } from '@/components/upload/uploadFragments'
import useUploads from '@/composables/useUploads'

const { t } = useI18n()

let searchQuery = reactive<UploadQuery>({
  nameContains: undefined,
  inContentTypes: undefined,
  notInContentTypes: ['model/gltf-binary'],
  sortBy: UploadSortField.CreatedAt,
  sortDirection: UploadSortDirection.Desc,
  results: 24,
  page: 0,
})

const imageOptions: ImageOptions = {
  height: 200,
  width: 200,
}

const fetchQuery = useQuery(
  gql`
    query UploadViewData($query: UploadQuery!, $imageOptions: ImageOptions!) {
      upload {
        search(query: $query) {
          id
          name
          contentType
          createdAt
          extension {
            ... on ImageExtension {
              url(options: $imageOptions)
            }
          }
          usageLocations {
            ...UploadUsageLocationDisplayDetails
          }
        }
      }
    }

    ${uploadUsageLocationDisplayDetailsFragments}
  `,
  () => ({
    query: searchQuery,
    imageOptions,
  }),
  {
    throttle: 300,
  },
)

const items = computed(() => fetchQuery.result.value?.upload.search || [])
const currentPage = computed({
  get: () => searchQuery.page + 1,
  set: (page: number) => {
    searchQuery.page = page - 1
  },
})
const totalPages = computed(() => {
  if (fetchQuery.loading.value) {
    return currentPage.value
  }
  if (items.value.length < searchQuery.results) {
    return currentPage.value
  }
  return currentPage.value + 1
})

type UploadItem = (typeof items.value)[0]

const deleteDialog = ref<InstanceType<typeof AsyncDeleteDialog> | null>(null)
const deleteMutation = useMutation(gql`
  mutation UploadViewDelete($id: ID!) {
    upload {
      deleteUpload(id: $id)
    }
  }
`)

const doDelete = async (upload: UploadItem) => {
  await deleteDialog.value?.open(upload.name, () =>
    deleteMutation.mutate({ id: upload.id }).then(() => fetchQuery.refetch()),
  )
}

const editUploadModel = ref<UploadItem | undefined>()
const updateMutation = useMutation(gql`
  mutation UploadViewUpdate($command: UpdateUpload!) {
    upload {
      updateUpload(command: $command) {
        id
        name
      }
    }
  }
`)
const doEdit = (upload: UploadItem) => {
  editUploadModel.value = upload
}

const onSave = (command: UpdateUpload) => {
  updateMutation
    .mutate({ command })
    .then(() => fetchQuery.refetch())
    .finally(() => {
      editUploadModel.value = undefined
    })
}

const { addUploadDoneListener } = useUploads()
onUnmounted(
  addUploadDoneListener(() => {
    searchQuery.page = 0
    fetchQuery.refetch()
  }),
)
</script>

<template>
  <h1>{{ t('entity.upload.plural') }}</h1>

  <v-row class="mb-4" align="stretch">
    <v-col cols="6" class="d-flex">
      <upload-search v-model="searchQuery" class="flex-grow-1" />
    </v-col>
    <v-col cols="6" class="d-flex">
      <v-card class="flex-grow-1">
        <v-card-text>
          <upload-button :mime-types="[]" />
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <div class="upload-grid">
    <upload-card
      v-for="upload in items"
      :key="upload.id"
      :upload="upload"
      :image-options="imageOptions"
      class="upload-item"
      @edit="doEdit"
      @delete="doDelete"
    />
    <div
      v-if="items.length === 0 && !fetchQuery.loading.value"
      class="text-center text-grey upload-no-data"
    >
      {{ t('component.uploadPicker.tableNoData') }}
    </div>
  </div>

  <v-pagination
    v-if="totalPages > 1"
    v-model="currentPage"
    :length="totalPages"
    :total-visible="7"
    :loading="fetchQuery.loading.value"
    class="mt-4"
  />

  <async-delete-dialog ref="deleteDialog" />
  <edit-upload v-model="editUploadModel" @save="onSave" />
</template>

<style scoped lang="scss">
.upload-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;

  .upload-item {
    min-width: 0;
  }

  .upload-no-data {
    grid-column: 1 / -1;
  }
}
</style>
